import { useState, useEffect } from 'react';
import api from '@/utils/api';
import { toast } from 'sonner';
import { 
  Mail, Send, Users, Calendar, FileText, RefreshCw, 
  Eye, Trash2, CheckCircle, Clock, AlertCircle
} from 'lucide-react';

export default function NewsletterManagement() {
  const [loading, setLoading] = useState(true);
  const [subscribers, setSubscribers] = useState([]);
  const [history, setHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('compose');
  const [sending, setSending] = useState(false);
  const [newsletter, setNewsletter] = useState({
    subject: '',
    content: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [subsRes, histRes] = await Promise.all([
        api.get('/newsletter/subscribers'),
        api.get('/newsletter/history').catch(() => ({ data: [] }))
      ]);
      setSubscribers(subsRes.data || []);
      setHistory(histRes.data || []);
    } catch (err) {
      console.error('Error fetching newsletter data:', err);
    } finally {
      setLoading(false);
    }
  };

  const sendNewsletter = async () => {
    if (!newsletter.subject.trim() || !newsletter.content.trim()) {
      toast.error('Preencha o assunto e o conteúdo da newsletter');
      return;
    }
    if (subscribers.length === 0) {
      toast.error('Não existem subscritores ativos');
      return;
    }
    
    setSending(true);
    try {
      await api.post(`/newsletter/send?subject=${encodeURIComponent(newsletter.subject)}&content=${encodeURIComponent(newsletter.content)}`);
      toast.success(`Newsletter enviada para ${subscribers.length} subscritores`);
      setNewsletter({ subject: '', content: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao enviar newsletter');
    } finally {
      setSending(false);
    }
  };

  const templates = [
    {
      name: 'Notícia Geral',
      subject: 'Novidades da Embaixada de Angola',
      content: '<h2>Caros Cidadãos,</h2><p>Temos o prazer de informar...</p><p>Atenciosamente,<br>Embaixada de Angola em Espanha</p>'
    },
    {
      name: 'Comunicado Oficial',
      subject: 'Comunicado Oficial da Embaixada',
      content: '<h2>Comunicado</h2><p>A Embaixada de Angola em Espanha informa que...</p><p>Data: [DATA]</p>'
    },
    {
      name: 'Evento Cultural',
      subject: 'Convite para Evento Cultural',
      content: '<h2>Convite Especial</h2><p>Convidamos V. Exa. para participar no evento...</p><p>Local: [LOCAL]<br>Data: [DATA]<br>Hora: [HORA]</p>'
    }
  ];

  const applyTemplate = (template) => {
    setNewsletter({
      subject: template.subject,
      content: template.content
    });
    toast.success('Template aplicado');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-gray-400" size={24} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="newsletter-management">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestão de Newsletter</h1>
          <p className="text-sm text-gray-500 mt-1">
            {subscribers.length} subscritor(es) ativo(s)
          </p>
        </div>
        <button
          onClick={fetchData}
          className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50"
        >
          <RefreshCw size={18} className="text-gray-600" />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {[
          { id: 'compose', label: 'Compor', icon: FileText },
          { id: 'subscribers', label: 'Subscritores', icon: Users },
          { id: 'history', label: 'Histórico', icon: Clock },
        ].map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id 
                  ? 'border-[#CC0000] text-[#CC0000]' 
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon size={16} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'compose' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Compose Form */}
          <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Compor Newsletter</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Assunto</label>
                <input
                  type="text"
                  value={newsletter.subject}
                  onChange={(e) => setNewsletter(n => ({ ...n, subject: e.target.value }))}
                  placeholder="Assunto da newsletter..."
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500/20"
                  data-testid="newsletter-subject"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Conteúdo (HTML)</label>
                <textarea
                  value={newsletter.content}
                  onChange={(e) => setNewsletter(n => ({ ...n, content: e.target.value }))}
                  placeholder="<h2>Título</h2><p>Conteúdo da newsletter...</p>"
                  rows={12}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500/20 font-mono"
                  data-testid="newsletter-content"
                />
              </div>
              
              <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                <p className="text-sm text-gray-500">
                  Será enviada para <strong>{subscribers.length}</strong> subscritores
                </p>
                <button
                  onClick={sendNewsletter}
                  disabled={sending || !newsletter.subject.trim() || !newsletter.content.trim()}
                  className="flex items-center gap-2 px-6 py-2 bg-[#CC0000] text-white text-sm font-medium rounded-lg hover:bg-[#A30000] disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="send-newsletter-btn"
                >
                  {sending ? (
                    <RefreshCw size={16} className="animate-spin" />
                  ) : (
                    <Send size={16} />
                  )}
                  {sending ? 'A enviar...' : 'Enviar Newsletter'}
                </button>
              </div>
            </div>
          </div>

          {/* Templates */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="font-semibold text-gray-900 mb-4">Templates</h3>
            <div className="space-y-3">
              {templates.map((template, idx) => (
                <button
                  key={idx}
                  onClick={() => applyTemplate(template)}
                  className="w-full p-3 text-left border border-gray-200 rounded-lg hover:border-[#CC0000] hover:bg-red-50/30 transition-colors"
                >
                  <p className="text-sm font-medium text-gray-900">{template.name}</p>
                  <p className="text-xs text-gray-500 mt-0.5 truncate">{template.subject}</p>
                </button>
              ))}
            </div>
            
            <div className="mt-6 p-4 bg-amber-50 rounded-lg">
              <p className="text-xs text-amber-800">
                <strong>Dica:</strong> Use HTML básico no conteúdo. O sistema adiciona automaticamente 
                o cabeçalho e rodapé da Embaixada.
              </p>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'subscribers' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users size={18} className="text-gray-500" />
              <span className="font-medium text-gray-900">Subscritores Ativos</span>
            </div>
            <span className="text-sm text-gray-500">{subscribers.length} total</span>
          </div>
          <div className="divide-y divide-gray-100 max-h-[500px] overflow-y-auto">
            {subscribers.length === 0 ? (
              <div className="p-8 text-center text-gray-400">
                <Mail size={40} className="mx-auto mb-3 opacity-30" />
                <p>Nenhum subscritor ativo</p>
              </div>
            ) : (
              subscribers.map((sub, idx) => (
                <div key={idx} className="flex items-center justify-between px-4 py-3 hover:bg-gray-50">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                      <Mail size={14} className="text-gray-500" />
                    </div>
                    <span className="text-sm text-gray-900">{sub.email}</span>
                  </div>
                  <span className="text-xs text-gray-400">
                    {sub.subscribed_at && new Date(sub.subscribed_at).toLocaleDateString('pt-PT')}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {activeTab === 'history' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center gap-2">
              <Clock size={18} className="text-gray-500" />
              <span className="font-medium text-gray-900">Histórico de Envios</span>
            </div>
          </div>
          <div className="divide-y divide-gray-100">
            {history.length === 0 ? (
              <div className="p-8 text-center text-gray-400">
                <Send size={40} className="mx-auto mb-3 opacity-30" />
                <p>Nenhuma newsletter enviada</p>
              </div>
            ) : (
              history.map((item, idx) => (
                <div key={idx} className="p-4 hover:bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium text-gray-900">{item.subject}</p>
                      <p className="text-sm text-gray-500 mt-1">
                        Enviada para {item.recipient_count} subscritores
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        item.status === 'sent' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                      }`}>
                        {item.status === 'sent' ? 'Enviada' : 'A enviar'}
                      </span>
                      <span className="text-xs text-gray-400">
                        {new Date(item.sent_at).toLocaleDateString('pt-PT')}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
